import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, ExternalLink } from 'lucide-react';
import { getBilling, startBillingCheckout, openBillingPortal } from '../api';

// Settings > Billing for a customer workspace, also used on the locked
// screen. Billing is per user per month through Stripe. Card details never
// reach our server: Subscribe goes to Stripe Checkout and Manage billing to
// Stripe's customer portal, both by sending the whole page to the URL the
// server returns. Stripe sends the person back to
// /settings?section=billing&billing=success (or =cancelled).

// How long to wait after Stripe sends someone back before reading the
// status again. Stripe tells our server by webhook, which takes a moment.
export const REFRESH_AFTER_CHECKOUT_MS = 3000;

const parseDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

// "Nov 15", or "Nov 15, 2027" when it is not this year.
export const shortDate = (iso) => {
  const date = parseDate(iso);
  if (!date) return '';
  const opts = { month: 'short', day: 'numeric' };
  if (date.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return date.toLocaleDateString(undefined, opts);
};

// There is no info alert style, so neutral notes get a plain panel.
const NEUTRAL_NOTE = {
  padding: '12px 16px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
};
const noteProps = (tone) => (tone === 'info' ? { style: NEUTRAL_NOTE } : { className: `alert alert-${tone}` });

const users = (n) => `${n} ${Number(n) === 1 ? 'user' : 'users'}`;

// "$12.00 per user per month"
export const priceText = (price) => {
  if (!price || price.unit_amount == null) return '';
  let amount;
  try {
    amount = new Intl.NumberFormat(undefined, { style: 'currency', currency: String(price.currency || 'usd').toUpperCase() })
      .format(Number(price.unit_amount) / 100);
  } catch (e) {
    amount = `${(Number(price.unit_amount) / 100).toFixed(2)} ${String(price.currency || '').toUpperCase()}`;
  }
  return `${amount} per user per ${price.interval || 'month'}`;
};

const LOCKED_REASONS = {
  trial_ended: 'Your workspace is locked because the trial ended',
  canceled: 'Your workspace is locked because the subscription was canceled',
  unpaid: 'Your workspace is locked because payment failed',
  incomplete: "Your workspace is locked because the first payment didn't go through",
  paused: 'Your workspace is locked because the subscription is paused',
};

// The plain-language status line, and its tone for the alert around it.
export function billingStatus(b) {
  if (!b) return { text: '', tone: 'info' };
  if (b.locked) {
    return { text: LOCKED_REASONS[b.reason] || 'Your workspace is locked until it has an active subscription', tone: 'error' };
  }
  const seats = b.billed_seats ?? b.seats;
  const periodEnd = shortDate(b.current_period_end);
  switch (b.state) {
    case 'exempt':
      return { text: "This workspace isn't billed", tone: 'info' };
    case 'free':
      return { text: 'Free plan', tone: 'success' };
    case 'trial': {
      const ends = shortDate(b.trial_ends_at);
      return { text: ends ? `Free trial until ${ends}` : 'Free trial', tone: 'info' };
    }
    case 'past_due':
      return { text: 'Payment failed: update your card to keep access', tone: 'warning' };
    case 'active':
      if (b.cancel_at_period_end) {
        return { text: periodEnd ? `Cancels at the end of the period on ${periodEnd}` : 'Cancels at the end of the period', tone: 'warning' };
      }
      return {
        text: `Subscribed${seats != null ? `: ${users(seats)}` : ''}${periodEnd ? `, renews ${periodEnd}` : ''}`,
        tone: 'success',
      };
    default:
      return { text: 'No subscription yet', tone: 'info' };
  }
}

// Subscribe when there is nothing to manage yet, or the old subscription
// is over; otherwise the portal is where the card and invoices live.
const needsCheckout = (b) =>
  !b.has_subscription || ['canceled', 'incomplete_expired'].includes(b.subscription_status);

export default function BillingPanel({ compact = false, onLoaded }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [returnNote, setReturnNote] = useState('');
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const res = await getBilling();
      setData(res.data);
      if (onLoadedRef.current) onLoadedRef.current(res.data);
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Could not load billing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Coming back from Stripe: say what happened once, take the flag off the
  // address so a reload does not say it again, and after a successful
  // checkout read the status once more when the webhook has had time.
  useEffect(() => {
    const outcome = searchParams.get('billing');
    if (!outcome) return undefined;
    setReturnNote(outcome === 'success' ? 'success' : outcome === 'cancelled' ? 'cancelled' : '');
    const next = new URLSearchParams(searchParams);
    next.delete('billing');
    setSearchParams(next, { replace: true });
    if (outcome !== 'success') return undefined;
    const timer = setTimeout(load, REFRESH_AFTER_CHECKOUT_MS);
    return () => clearTimeout(timer);
  }, []); // Read the address once, when the panel opens.

  const goToStripe = async (kind) => {
    setBusy(kind);
    setError('');
    try {
      const res = await (kind === 'checkout' ? startBillingCheckout() : openBillingPortal());
      const url = res.data?.url;
      if (typeof url !== 'string' || !url) throw new Error('no url');
      window.location.assign(url);
    } catch (err) {
      setError(err.response?.data?.error || (kind === 'checkout'
        ? 'Could not start the subscription. Try again in a moment.'
        : 'Could not open billing. Try again in a moment.'));
      setBusy('');
    }
  };

  const notes = (
    <>
      {returnNote === 'success' && (
        <div className="alert alert-success" role="status" style={{ marginBottom: '16px' }}>
          Thanks. Your subscription is being set up; this page updates in a moment.
        </div>
      )}
      {returnNote === 'cancelled' && (
        <div role="status" style={{ ...NEUTRAL_NOTE, marginBottom: '16px' }}>
          Checkout was cancelled. Nothing was charged.
        </div>
      )}
      {error && <div className="alert alert-error" role="alert" style={{ marginBottom: '16px' }}>{error}</div>}
    </>
  );

  let body;
  if (loading) {
    body = <div className="loading-container"><div className="spinner"></div></div>;
  } else if (loadError) {
    body = <div className="alert alert-error" role="alert">{loadError}</div>;
  } else if (!data) {
    body = null;
  } else if (data.enabled === false) {
    body = <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Billing isn't switched on for this server yet.</p>;
  } else {
    const status = billingStatus(data);
    const price = priceText(data.price);
    const noCharge = data.state === 'free' || data.state === 'exempt';
    const checkout = needsCheckout(data);
    body = (
      <>
        <div {...noteProps(status.tone)} role="status" style={{ ...(noteProps(status.tone).style || {}), marginBottom: '16px', fontWeight: 600 }}>
          {status.text}.
        </div>
        {!noCharge && (
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: '0 0 16px', fontSize: '14px' }}>
            {price && (
              <>
                <dt style={{ color: 'var(--text-secondary)' }}>Price</dt>
                <dd style={{ margin: 0, fontWeight: 600 }}>{price}</dd>
              </>
            )}
            <dt style={{ color: 'var(--text-secondary)' }}>Active users</dt>
            <dd style={{ margin: 0, fontWeight: 600 }}>{data.seats ?? 0}</dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Billed users</dt>
            <dd style={{ margin: 0, fontWeight: 600 }}>{data.billed_seats ?? 'None yet'}</dd>
          </dl>
        )}
        {!noCharge && (data.can_manage ? (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {checkout ? (
              <button type="button" className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => goToStripe('checkout')}>
                <CreditCard size={16} /> {busy === 'checkout' ? 'Opening Stripe...' : 'Subscribe'}
              </button>
            ) : (
              <button type="button" className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => goToStripe('portal')}>
                <ExternalLink size={16} /> {busy === 'portal' ? 'Opening Stripe...' : 'Manage billing'}
              </button>
            )}
            {checkout && data.has_subscription && (
              <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => goToStripe('portal')}>
                {busy === 'portal' ? 'Opening Stripe...' : 'Past invoices'}
              </button>
            )}
          </div>
        ) : (
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Ask your administrator to manage billing.</p>
        ))}
        {!noCharge && data.can_manage && (
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '12px 0 0' }}>
            Payment is handled by Stripe. Your card details are never stored here.
          </p>
        )}
      </>
    );
  }

  if (compact) {
    return <div>{notes}{body}</div>;
  }

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h3 className="card-title">Billing</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Your subscription is charged per active user each month.
          </p>
        </div>
      </div>
      <div className="card-body">
        {notes}
        {body}
      </div>
    </div>
  );
}
